import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import type { ComponentProps } from 'react';
import { createContext, useContext, useId, useLayoutEffect, useRef, useState } from 'react';
import { mergeStyling } from '../common-functions';

const PopoverIdContext = createContext<string | null>(null);

const Popover = ({ ...props }: PopoverPrimitive.Root.Props) => {
  const id = useId();
  return (
    <PopoverIdContext value={id}>
      <PopoverPrimitive.Root data-slot="popover" {...props} />
    </PopoverIdContext>
  );
};

const PopoverTrigger = ({ className, ...props }: PopoverPrimitive.Trigger.Props) => {
  const popoverId = useContext(PopoverIdContext);
  return (
    <PopoverPrimitive.Trigger
      data-slot="popover-trigger"
      data-popover-id={popoverId}
      className={mergeStyling('outline-hidden focus-visible:outline-2 focus-visible:outline-primary-1', className)}
      {...props}
    />
  );
};

const PopoverClose = ({ ...props }: PopoverPrimitive.Close.Props) => {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
};

type PopoverAlign = PopoverPrimitive.Positioner.Props['align'];
type PopoverSide = PopoverPrimitive.Positioner.Props['side'];

const PopoverContent = ({
  className,
  align = 'center',
  alignOffset = 0,
  side = 'bottom',
  sideOffset = 4,
  collisionAvoidance,
  container,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<PopoverPrimitive.Positioner.Props, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'collisionAvoidance'> &
  Pick<PopoverPrimitive.Portal.Props, 'container'>) => {
  const popoverId = useContext(PopoverIdContext);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | undefined>(undefined);
  const detectionDoneRef = useRef(false);

  // Auto-detect parent dialog using the unique popover ID to find the correct trigger.
  // We render multiple hidden dialogs at once, so we need to ensure we target the correct dialog.
  useLayoutEffect(() => {
    if (container !== undefined) return;
    if (detectionDoneRef.current || !popoverId) return;

    detectionDoneRef.current = true;

    const triggerElement: HTMLElement | null = document.querySelector(
      `[data-slot="popover-trigger"][data-popover-id="${popoverId}"]`
    );

    if (!triggerElement) return;

    const dialogAncestor = triggerElement.closest('dialog');
    if (dialogAncestor) {
      setPortalContainer(dialogAncestor);
    } else {
      setPortalContainer(undefined);
    }
  }, [popoverId, container]);

  return (
    <PopoverPrimitive.Portal container={container !== undefined ? container : portalContainer}>
      <PopoverPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionAvoidance={collisionAvoidance}
        className="isolate z-50"
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={mergeStyling(
            'z-50 flex w-72 flex-col gap-2.5 rounded-sm bg-mono-1 p-2.5 text-mono-0 text-sm shadow-md outline-hidden ring-1 ring-gray-4 duration-100',
            className
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
};

const PopoverHeader = ({ className, ...props }: ComponentProps<'div'>) => {
  return (
    <div data-slot="popover-header" className={mergeStyling('flex flex-col gap-1 text-sm', className)} {...props} />
  );
};

const PopoverTitle = ({ className, ...props }: PopoverPrimitive.Title.Props) => {
  return (
    <PopoverPrimitive.Title
      data-slot="popover-title"
      className={mergeStyling('font-medium text-base', className)}
      {...props}
    />
  );
};

const PopoverDescription = ({ className, ...props }: PopoverPrimitive.Description.Props) => {
  return (
    <PopoverPrimitive.Description
      data-slot="popover-description"
      className={mergeStyling('text-gray-2', className)}
      {...props}
    />
  );
};

export {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  type PopoverAlign,
  type PopoverSide,
};
